import { useCallback, useMemo, useState } from 'react';

export enum PCPStep {
  ConfigureStep = 1,
  DistanceStep = 2,
  DownPaymentStep = 3,
}

export const stepNameMap: Record<PCPStep, string> = {
  [PCPStep.ConfigureStep]: 'configure',
  [PCPStep.DistanceStep]: 'distance',
  [PCPStep.DownPaymentStep]: 'downpayment',
};

export function useWizardSteps({
  hasOnlyOneTerm,
  isDownPaymentEnabled,
  onFinish,
}: {
  hasOnlyOneTerm: boolean;
  isDownPaymentEnabled: boolean;
  onFinish: () => void;
}) {
  const steps = useMemo(() => {
    // if has only one term, skip "ConfigureStep"
    const base = hasOnlyOneTerm
      ? [PCPStep.DistanceStep]
      : [PCPStep.ConfigureStep, PCPStep.DistanceStep];

    if (isDownPaymentEnabled) base.push(PCPStep.DownPaymentStep);

    return base;
  }, [isDownPaymentEnabled, hasOnlyOneTerm]);

  const [{ prevStep, currentStep }, setStep] = useState<{
    prevStep: PCPStep | null;
    currentStep: PCPStep;
  }>({ prevStep: null, currentStep: steps[0] });

  const goNext = useCallback(() => {
    const idx = steps.indexOf(currentStep);
    if (idx < steps.length - 1) {
      setStep({
        prevStep: currentStep,
        currentStep: steps[idx + 1],
      });
    } else {
      onFinish();
    }
  }, [currentStep, steps, onFinish]);

  const goBack = useCallback(() => {
    const idx = steps.indexOf(currentStep);
    if (idx > 0)
      setStep({
        prevStep: currentStep,
        currentStep: steps[idx - 1],
      });
  }, [currentStep, steps]);

  const progress = ((steps.indexOf(currentStep) + 1) / steps.length) * 100;

  return {
    currentStep,
    prevStep,
    goNext,
    goBack,
    progress,
    isLastStep: currentStep === steps[steps.length - 1],
    isFirstStep: currentStep === steps[0],
  };
}
